import React, { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import systemPromptState from "../../../recoil/SystemPrompt";
import MessagesContainer from "./MessagesContainer";
import InputBar from "./InputBar";
import { fetchTextMessage, generateImage, fetchSessionData, createSession } from "../../../services/api";
import { EventSourceMessage } from "@microsoft/fetch-event-source";
import { useParams } from "react-router-dom";
import { Message } from "../../../types/Message";
import selectedTypeState from "../../../recoil/SelectedType";
import engineState from "../../../recoil/SelectedEngine";
import { axiosClient } from "../../../services/axiosClient";
import ThemeMode from "../../../recoil/ThemeMode";
import session from "../../../recoil/Session";

const ChatWindow: React.FC = () => {
  const selectedType = useRecoilValue(selectedTypeState);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputEnabled, setInputEnabled] = useState<boolean>(true);
  const [systemPrompt, setSystemPrompt] = useRecoilState(systemPromptState);
  const mode = useRecoilValue(ThemeMode);
  const selectedEngine = useRecoilValue(engineState);
  const { session_id } = useParams<{ session_id: string }>(); // Extract UUID from the URL
  const params = useParams()
  axiosClient.setHeader('session', session_id!);
  const [state, setState] = useRecoilState(session);

  const controller = useRef<AbortController>(new AbortController());

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!axiosClient.isAuthenticated()) return

        const response = await fetchSessionData();
        if (response && response.data && response.data.messages.length) {
          let system: Message | null = null;
          let tmp = response.data.messages;

          // iterate the messages and add a property 'text' that has the
          // same value as the message.content
          tmp.forEach((message: Message) => {
            message.text = message.content!;
            message.type = message.metadata?.type ?? "text";
            message.author = message.role as "user" | "assistant" | "system";
            message.images = message.metadata?.images ?? [];
            message.uuid = message.uuid
            if (message.author === "system") {
              system = message;
            }
          });
          setMessages(tmp);
          if (system !== null) {
            setSystemPrompt({
              changed: false,
              role: "system",
              content: (system as Message).content || "You are Taotensor, a helpful assistant!",
            });
          }
        }
      } catch (error) {
        console.error('Error fetching session data:', error);
      }
    };

    fetchData();
  }, [session_id]);

  const handleSendMessage = (newMessage: string, variations: number) => {
    if (!messages.length) {
      createSession();
    }

    setMessages((prevState) => [
      ...prevState,
      { author: "user", text: newMessage, type: "text" },
    ]);
    switch (selectedType) {
      case "text":
      case "twitter":
        setMessages((prevState) => [
          ...prevState,
          {
            author: "assistant",
            text: "",
            type: "text-loading",
          },
        ]);
        break;
      case "image":
        setMessages((prevState) => [
          ...prevState,
          { author: "assistant", text: "", type: "image-loading" },
        ]);
        setInputEnabled(false);
        generateImage(newMessage, variations, selectedEngine.image)
          .then((data) => {
            setMessages((prevState) => [
              ...prevState.slice(0, -1),
              { author: "assistant", text: data.data, type: "image", images: data.data.metadata?.images },
            ]);
          })
          .catch(() => {
            setMessages((prevState) => [
              ...prevState.slice(0, -1),
              {
                author: "assistant",
                text: "Can't generate image. Please try again",
                type: "error",
              },
            ]);
          })
          .finally(() => {
            setInputEnabled(true);
          });
        break;
    }
  };

  useEffect(() => {
    const onopen = (res: Response) => {
      if (res.ok && res.status === 200) {
        setMessages((prevState) => [
          ...prevState.slice(0, -1),
          {
            author: "assistant",
            text: "",
            type: "text",
          },
        ]);
        setInputEnabled(false);
      } else {
        setMessages((prevState) => [
          ...prevState.slice(0, -1),
          {
            author: "assistant",
            text: "Can't generate text. Please try again",
            type: "error",
          },
        ]);
      }
    };

    const onmessage = (event: EventSourceMessage) => {
      const eventData = JSON.parse(event.data);

      const pathSegments = window.location.pathname.split('/');
      const sessionIndex = pathSegments.indexOf('chat');
      const session_id = sessionIndex !== -1 ? pathSegments[sessionIndex + 1] : null;

      if (eventData.session && eventData.session !== session_id) {
        return;
      }

      setMessages((prevState) => [
        ...prevState.slice(0, -1),
        {
          ...prevState[prevState.length - 1],
          text: prevState[prevState.length - 1].text + eventData.text,
        },
      ]);
    };

    const onmessageTwitter = (event: EventSourceMessage) => {
      setMessages((prevState) => [
        ...prevState.slice(0, -1),
        {
          ...prevState[prevState.length - 1],
          text: prevState[prevState.length - 1].text + event.data,
        },
      ]);
    };

    const onerror = (err: any) => {
      if (messages[messages.length - 1]?.type === "text-loading") {
        setMessages((prevState) => [
          ...prevState.slice(0, -1),
          {
            author: "assistant",
            text: "Error during text generation. Please try again",
            type: "error",
          },
        ]);
      } else {
        setMessages((prevState) => [
          ...prevState,
          {
            author: "assistant",
            text: "Error during text generation. Please try again",
            type: "error",
          },
        ]);
      }
      throw new Error();
    };

    const onclose = () => {
      setInputEnabled(true);
      const pathSegments = window.location.pathname.split('/');
      const sessionIndex = pathSegments.indexOf('chat');
      const session_id = sessionIndex !== -1 ? pathSegments[sessionIndex + 1] : null;

      setState((prevState) => ({
        ...prevState,
        session: session_id,
        isClosed: true
      }));
    };

    if (
      messages[messages.length - 1]?.author === "assistant" &&
      messages[messages.length - 1]?.type === "text-loading"
    ) {
      let messagesToSend = [];
      if (systemPrompt.changed) {
        messagesToSend.push(
          {
            role: systemPrompt.role,
            content: systemPrompt.content,
          }
        );
        setSystemPrompt((prevState) => ({
          ...prevState,
          changed: false,
        }));
      }
      messagesToSend.push({
        role: 'user',
        content: messages[messages.length - 2].text,
      });

      fetchTextMessage(
        selectedType,
        session_id!,
        messagesToSend,
        selectedEngine.text,
        onopen,
        selectedType === 'twitter' ? onmessageTwitter : onmessage,
        onerror,
        onclose,
        controller.current.signal
      ).catch(() => setInputEnabled(true));
    }
  }, [messages, session_id]);

  return (
    <div className="flex-1 p:2 mr-2 sm:p-6 justify-between flex flex-col h-full w-full relative">
      <MessagesContainer messages={messages} onSendMessage={handleSendMessage} />
      <InputBar
        onSendMessage={handleSendMessage}
        enabled={inputEnabled}
        setEnabled={setInputEnabled}
        controller={controller}
      />
    </div>
  );
};

export default ChatWindow;